// Panel de "Capas" (estilo Canva): lista las capas de la escena de FRENTE (arriba)
// a FONDO (abajo), permite reordenarlas ARRASTRANDO (@dnd-kit) o con los botones
// rápidos (al frente / adelante / atrás / al fondo) para la capa seleccionada, y
// seleccionar una capa tocándola.
//
// La escena guarda las capas en `scene.layers` con el último = frente. La lista se
// muestra invertida; al soltar un drag se traduce a índices del array real.

import { useEffect, useRef } from 'react';
import { Check, ChevronDown, ChevronsDown, ChevronsUp, ChevronUp, GripVertical } from 'lucide-react';
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { PanelSheet } from './PanelSheet';
import type { Layer } from '../scene/types';
import type { SceneApi } from '../scene/useScene';

const SHAPE_LABEL: Record<string, string> = {
  rect: 'Rectángulo',
  circle: 'Círculo',
  triangle: 'Triángulo',
  pentagon: 'Pentágono',
  hexagon: 'Hexágono',
  star: 'Estrella',
  line: 'Línea',
  arrow: 'Flecha',
};

function layerLabel(l: Layer): string {
  if (l.type === 'text') return l.text?.trim() || 'Texto';
  if (l.type === 'image') return 'Imagen';
  return SHAPE_LABEL[(l as { kind: string }).kind] ?? 'Forma';
}

// `sm` = versión chica para el rótulo de "capa seleccionada" que va sobre los botones
// de orden; sin `sm`, la miniatura de la fila de la lista.
function LayerThumb({ l, sm }: { l: Layer; sm?: boolean }) {
  const box = sm ? 'w-5 h-5 rounded-[3px]' : 'w-8 h-8 rounded';
  if (l.type === 'image') {
    return <img src={l.src} alt="" className={`${box} object-cover border border-neutral-200 shrink-0`} />;
  }
  if (l.type === 'shape') {
    const s = l as { fill: string; stroke: string; kind: string };
    return (
      <span
        className={`${box} shrink-0 border border-neutral-200`}
        style={{
          background: s.kind === 'line' ? 'transparent' : s.fill,
          borderColor: s.kind === 'line' ? s.stroke : undefined,
        }}
      />
    );
  }
  return (
    <span
      className={`${box} shrink-0 border border-neutral-200 bg-neutral-50 flex items-center justify-center font-bold text-neutral-500 ${
        sm ? 'text-[10px]' : 'text-[13px]'
      }`}
    >
      T
    </span>
  );
}

function SortableRow({
  l,
  selected,
  onSelect,
}: {
  l: Layer;
  selected: boolean;
  onSelect: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: l.id });
  return (
    <div
      ref={setNodeRef}
      // `data-selected` lo usa el efecto de scroll-into-view al abrir el panel.
      data-selected={selected || undefined}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      // Selección: hex arbitrario, NO `border-brand-primary/50 bg-brand-primary/[0.06]`
      // — `brand-primary` es un `var()` y Tailwind 3 no puede aplicarle el modificador
      // de opacidad, así que esas clases no compilaban y la fila seleccionada se veía
      // IGUAL que las demás. `#1644EB` es el mismo azul del marco de selección del
      // lienzo (CanvasStage). Ver ADR-2026-08-04-editor-canvas-ajustes-mobile.
      className={`flex items-center gap-2 p-1.5 rounded-md border transition-colors ${
        selected ? 'border-[#1644EB] bg-[#1644EB0F]' : 'border-neutral-200 bg-surface-card hover:bg-neutral-50'
      } ${isDragging ? 'opacity-70 shadow-md' : ''}`}
    >
      {/* Barra de acento: se renderiza SIEMPRE (transparente si no está seleccionada)
          para que la fila no cambie de ancho al seleccionarla. Redunda con el fondo
          tenue a propósito: en mobile, al sol, ese 6% de tinte no se ve. */}
      <span
        className={`w-1 self-stretch rounded-full shrink-0 ${selected ? 'bg-[#1644EB]' : 'bg-transparent'}`}
        aria-hidden
      />
      <button
        type="button"
        className="w-6 h-8 flex items-center justify-center text-neutral-400 hover:text-neutral-600 cursor-grab active:cursor-grabbing touch-none shrink-0"
        aria-label="Arrastrar para reordenar"
        {...attributes}
        {...listeners}
      >
        <GripVertical className="w-4 h-4" />
      </button>
      <button
        type="button"
        onClick={onSelect}
        aria-pressed={selected}
        className="flex-1 min-w-0 flex items-center gap-2 text-left"
      >
        <LayerThumb l={l} />
        <span className={`text-[13px] truncate ${selected ? 'text-[#1644EB] font-medium' : 'text-neutral-700'}`}>
          {layerLabel(l)}
        </span>
      </button>
      {selected && <Check className="w-4 h-4 text-[#1644EB] shrink-0 mr-1" aria-hidden />}
    </div>
  );
}

interface Props {
  api: SceneApi;
  onClose: () => void;
}

export function CapasPanel({ api, onClose }: Props) {
  const layers = api.scene.layers;
  const selId = api.scene.selectedId;
  const sel = api.selected;
  // Vista: frente (último del array) arriba.
  const view = [...layers].reverse();
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Al ABRIR el panel, traer la fila seleccionada a la vista: si seleccionaste algo en
  // el lienzo y la escena tiene muchas capas, la fila marcada podía quedar fuera de
  // pantalla y el resaltado no servía para nada. Solo al montar — seguir a la selección
  // después pelearía con el drag & drop y con el scroll manual.
  const scrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    scrollRef.current?.querySelector('[data-selected]')?.scrollIntoView({ block: 'nearest' });
  }, []);

  const onDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const from = layers.findIndex((l) => l.id === active.id);
    const to = layers.findIndex((l) => l.id === over.id);
    if (from < 0 || to < 0) return;
    api.moveLayer(from, to);
  };

  const arrangeBtn =
    'flex-1 h-9 rounded-md border border-neutral-200 bg-surface-card hover:bg-neutral-50 disabled:opacity-40 disabled:pointer-events-none flex items-center justify-center gap-1.5 text-[12px] font-medium text-neutral-700 transition-colors';

  return (
    <PanelSheet title="Capas" onClose={onClose}>
      <div ref={scrollRef} className="flex-1 min-h-0 overflow-y-auto p-3 space-y-3">
        {/* Botones rápidos de orden para la capa seleccionada, con un rótulo que dice
            SOBRE QUÉ capa actúan: sin él los cuatro botones se leían como acciones
            sueltas y no se entendía que operan sobre la selección. */}
        <div className="flex items-center gap-2 min-w-0 min-h-[20px]">
          {sel ? (
            <>
              <span className="text-[12px] text-neutral-500 shrink-0">Capa:</span>
              <LayerThumb l={sel} sm />
              <span className="text-[12.5px] font-medium text-[#1644EB] truncate">{layerLabel(sel)}</span>
            </>
          ) : (
            <span className="text-[12px] text-neutral-400">Tocá una capa para ordenarla.</span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-1.5">
          <button type="button" className={arrangeBtn} disabled={!sel} onClick={() => sel && api.reorder(sel.id, 'front')}>
            <ChevronsUp className="w-4 h-4" /> Al frente
          </button>
          <button type="button" className={arrangeBtn} disabled={!sel} onClick={() => sel && api.reorder(sel.id, 'forward')}>
            <ChevronUp className="w-4 h-4" /> Adelante
          </button>
          <button type="button" className={arrangeBtn} disabled={!sel} onClick={() => sel && api.reorder(sel.id, 'backward')}>
            <ChevronDown className="w-4 h-4" /> Atrás
          </button>
          <button type="button" className={arrangeBtn} disabled={!sel} onClick={() => sel && api.reorder(sel.id, 'back')}>
            <ChevronsDown className="w-4 h-4" /> Al fondo
          </button>
        </div>

        {view.length === 0 ? (
          <p className="text-[13px] text-neutral-400 text-center mt-2">No hay capas todavía.</p>
        ) : (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={view.map((l) => l.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-1.5">
                {view.map((l) => (
                  <SortableRow key={l.id} l={l} selected={l.id === selId} onSelect={() => api.select(l.id)} />
                ))}
              </div>
            </SortableContext>
          </DndContext>
        )}
      </div>
    </PanelSheet>
  );
}
